import { ONE_DAY, ONE_SECOND } from 'infra/services/date';

/**
 * Escopo de uma Chave Registrada: a quem o valor pertence.
 * - `origin`: a origem inteira do navegador, igual para toda transportadora.
 * - `company`: a transportadora da Sessão do Painel desta aba.
 * - `tab`: só esta aba (sessionStorage).
 */
const BROWSER_STORAGE_SCOPE_ENUM = {
	ORIGIN: 'origin',
	COMPANY: 'company',
	TAB: 'tab',
} as const;

export type BrowserStorageScope =
	(typeof BROWSER_STORAGE_SCOPE_ENUM)[keyof typeof BROWSER_STORAGE_SCOPE_ENUM];

/**
 * Validade de um valor. Número é ms desde a gravação; função deriva do próprio
 * valor o instante absoluto (epoch ms ou Date) em que ele deixa de valer.
 */
type Ttl<T> = number | ((value: T) => number | Date);

/**
 * Toda chave declara exatamente uma política de crescimento. `maxEntries` só
 * existe para records: o módulo guarda a idade de cada entrada e descarta as
 * mais antigas acima do limite.
 */
type Growth<T> =
	| { ttl: Ttl<T>; maxEntries?: never }
	| (T extends Record<string, unknown>
			? { maxEntries: number; ttl?: never }
			: never);

export type KeyDeclaration<T> = {
	scope: BrowserStorageScope;
	/** Versão divergente na leitura devolve `defaultValue`. */
	version: number;
	defaultValue: T;
	/** Credencial: apagada ao sair da Sessão do Painel. Ausente = Preferência. */
	credential?: true;
} & Growth<T>;

const declareKey = <T>(declaration: KeyDeclaration<T>) => declaration;

const PANEL_JWT_RENEWAL_MS = 30 * ONE_SECOND;

/** Sessão do Gestor no Company Watcher. Vive no localStorage porque "entrar no painel" abre outra aba na mesma origem. */
export type CompanyWatcherSession = {
	token: string;
	managerName: string;
	isMaster: boolean;
};

/**
 * Registro único das Chaves Registradas. Estado persistido fora daqui não
 * existe: o nome da chave é o único endereço aceito pelo módulo.
 */
export const BROWSER_STORAGE_KEYS = {
	hiddenBalance: declareKey<boolean>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: false,
		ttl: 365 * ONE_DAY,
	}),
	statementHiddenValues: declareKey<boolean>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: false,
		ttl: 365 * ONE_DAY,
	}),
	/** Categoria da sidebar aberta ou fechada, por chave de categoria. */
	sidebarCategoryCollapse: declareKey<Record<string, boolean>>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: {},
		maxEntries: 50,
	}),
	/** Grupo de contratos aberto ou fechado, por `${aba}/${grupo}`. */
	groupCollapse: declareKey<Record<string, boolean>>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.COMPANY,
		version: 1,
		defaultValue: {},
		maxEntries: 300,
	}),
	/** Critério de agrupamento escolhido em uma listagem de contratos, por chave da listagem. */
	contractGroupBy: declareKey<Record<string, string>>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: {},
		maxEntries: 10,
	}),
	/** Filtros aplicados no drawer de uma listagem de contratos, por chave da listagem. */
	contractDrawerFilters: declareKey<
		Record<string, Record<string, string[] | string | null>>
	>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.COMPANY,
		version: 1,
		defaultValue: {},
		maxEntries: 20,
	}),
	/** Ids de notificação já alertados no navegador. Regravado a cada preview, então nunca cresce. */
	notificationAlertLedger: declareKey<number[]>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.COMPANY,
		version: 1,
		defaultValue: [],
		ttl: 7 * ONE_DAY,
	}),
	/** Instante ISO do último alerta do navegador, para o intervalo mínimo entre alertas. */
	lastBrowserNotificationAt: declareKey<string | null>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: null,
		ttl: ONE_DAY,
	}),
	/** Tours já concluídos neste navegador, por tipo. Cache do `tour_completed` da API. */
	tourCompleted: declareKey<Record<string, boolean>>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.COMPANY,
		version: 1,
		defaultValue: {},
		maxEntries: 50,
	}),
	/** Transportadora pulou o welcome; o ttl é a janela em que o pulo vale. */
	welcomeSkipped: declareKey<boolean>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.COMPANY,
		version: 1,
		defaultValue: false,
		ttl: ONE_DAY,
	}),
	financialReportViewed: declareKey<boolean>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.COMPANY,
		version: 1,
		defaultValue: false,
		ttl: 365 * ONE_DAY,
	}),
	/** JWT do Nexus. Vence 30s antes do `expiresAt` da API, que é a janela de renovação. */
	panelJwt: declareKey<{ jwt: string; expiresAt: string } | null>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.COMPANY,
		version: 1,
		defaultValue: null,
		credential: true,
		ttl: (value) =>
			value ? new Date(value.expiresAt).getTime() - PANEL_JWT_RENEWAL_MS : 0,
	}),
	/** Token FCM registrado para este navegador. */
	fcmToken: declareKey<string | null>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: null,
		credential: true,
		ttl: 365 * ONE_DAY,
	}),
	/** Instante ISO a partir do qual o gestor pode pedir outro link de acesso. */
	companyWatcherRetryDate: declareKey<string | null>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: null,
		ttl: ONE_DAY,
	}),
	/** Instante ISO a partir do qual se pode pedir outro código de redefinição de senha. */
	resetPasswordRetryDate: declareKey<string | null>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: null,
		ttl: ONE_DAY,
	}),
	/** Host da API escolhido no seletor de ambiente (só dev e staging). */
	apiOverride: declareKey<string | null>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: null,
		ttl: 365 * ONE_DAY,
	}),
	/** Consentimento de cookies para o Clarity. */
	trackerConsent: declareKey<boolean>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: false,
		ttl: 365 * ONE_DAY,
	}),
	/** Força o modo PWA via `?pwa=1`, para teste. */
	forcePwa: declareKey<boolean>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: false,
		ttl: 365 * ONE_DAY,
	}),
	pwaInstalledTracked: declareKey<boolean>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: false,
		ttl: 365 * ONE_DAY,
	}),
	companyWatcherSession: declareKey<CompanyWatcherSession | null>({
		scope: BROWSER_STORAGE_SCOPE_ENUM.ORIGIN,
		version: 1,
		defaultValue: null,
		credential: true,
		ttl: 30 * ONE_DAY,
	}),
};

export type BrowserStorageKey = keyof typeof BROWSER_STORAGE_KEYS;

export type BrowserStorageValue<K extends BrowserStorageKey> =
	(typeof BROWSER_STORAGE_KEYS)[K]['defaultValue'];
